import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowRight, ChevronDown, MapPin } from "lucide-react";
import { WHATSAPP_URL, PORTRAIT } from "@/data/content";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskedLine({ children, index, className }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        custom={index}
        variants={lineVariants}
        initial="hidden"
        animate="visible"
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-brand-navy"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-brand-dark via-brand-royal to-brand-navy" />
      <div className="absolute inset-0 bg-noise" aria-hidden="true" />

      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-brand-light/20 blur-3xl" />
        <div className="absolute -right-20 -top-20 h-[28rem] w-[28rem] rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-brand-royal/40 blur-3xl" />
        <div className="absolute right-[8%] top-[12%] hidden text-[22rem] font-display font-extrabold leading-none text-stroke-white select-none lg:block">
          40
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-24 pt-32 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:px-12 lg:pt-24">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 backdrop-blur-md"
          >
            <span className="h-2 w-2 rounded-full bg-brand-orange" />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-white">
              Candidata a Deputada Federal · PSB 40 · Bahia
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-white">
            <MaskedLine index={0} className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl">
              Rosangela
            </MaskedLine>
            <MaskedLine index={1} className="text-4xl sm:text-6xl lg:text-7xl xl:text-8xl text-brand-light">
              da Saúde
            </MaskedLine>
            <MaskedLine index={2} className="mt-4 text-xl sm:text-2xl lg:text-3xl normal-case font-bold text-white/90">
              Um mandato para <span className="text-brand-orange">cuidar e incluir</span>
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-5"
          >
            <div
              data-testid="hero-ballot-number"
              className="flex items-baseline gap-2 rounded-2xl bg-gradient-to-br from-brand-orange to-amber-500 px-6 py-3 shadow-2xl shadow-brand-orange/40"
            >
              <span className="text-xs font-bold uppercase tracking-widest text-white/90">Vote</span>
              <span className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">4030</span>
            </div>
            <p className="max-w-md text-base sm:text-lg leading-relaxed text-white/80">
              Saúde, educação e inclusão para o Extremo Sul da Bahia — com quem sempre serviu a comunidade de perto.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#propostas"
              data-testid="hero-cta-propostas"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm sm:text-base font-bold text-brand-dark shadow-xl transition-transform duration-200 hover:-translate-y-1"
            >
              Conheça nossas propostas
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 bg-white/5 px-7 py-4 text-sm sm:text-base font-bold text-white backdrop-blur-sm transition-colors duration-200 hover:border-brand-orange hover:bg-brand-orange/20"
            >
              <MessageCircle className="h-5 w-5" />
              Fale conosco
            </a>
          </motion.div>
        </div>

        <motion.div
          style={{ y: portraitY }}
          initial={{ opacity: 0, scale: 0.94, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto w-full max-w-sm lg:max-w-md"
        >
          <div className="absolute -inset-8 rounded-full bg-brand-light/25 blur-3xl" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-t-[10rem] rounded-b-[2rem] border-4 border-white/20 shadow-2xl">
            <img
              src={PORTRAIT}
              alt="Rosangela da Saúde, candidata a Deputada Federal 4030, sorrindo com os braços cruzados e camisa polo azul"
              className="h-auto w-full object-cover"
              data-testid="hero-portrait"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/50 via-transparent to-transparent" aria-hidden="true" />
          </div>

          <div className="animate-float-soft absolute -left-6 top-10 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-xl shadow-xl sm:-left-10">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-light">Serviço público</p>
            <p className="font-display text-2xl font-extrabold text-white">21 anos</p>
          </div>
          <div className="animate-float-soft absolute -right-4 bottom-16 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur-xl shadow-xl sm:-right-8" style={{ animationDelay: "1.4s" }}>
            <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-brand-light">
              <MapPin className="h-3 w-3" /> Extremo Sul
            </p>
            <p className="font-display text-lg font-extrabold text-white">Bahia</p>
          </div>
        </motion.div>
      </div>

      <motion.a
        href="#sobre"
        data-testid="hero-scroll-indicator"
        aria-label="Rolar para a seção Sobre"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-white/70 transition-colors hover:text-white"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ChevronDown className="h-7 w-7" />
        </motion.div>
      </motion.a>
    </section>
  );
}
